<template>
  <div class="market-switcher">
    <overlay
      key="marketSwitcherDrawer"
      block-class="market-switcher-drawer-overlay"
      :direction="drawerDirection"
      namespace="marketSwitcherDrawer"
      type="drawer"
    >
      <template #body="{ isActive }">
        <market-switcher-drawer
          v-if="getComponentVisible('market-switcher-drawer')"
          v-cloak
          :countries="countries"
          :form="form"
          :is-active="isActive"
          :languages="languages"
        />

        <!-- Market switcher drawer loading placeholder -->
        <div
          v-if="!getComponentActive('market-switcher-drawer')"
          class="market-switcher__drawer critical-component-hide"
        >
          <p
            class="market-switcher__drawer-title text-heading-m"
            v-text="$string('market_switcher.drawer.select_country')"
          />

          <div class="market-switcher__drawer-items">
            <span
              v-for="(_, index) of new Array(countries?.length)"
              :key="index"
              class="market-switcher__drawer-item"
              :style="{ animationDelay: `${index * 0.15}s` }"
              v-text="$string('accessibility.loading')"
            />
          </div>
        </div>
      </template>
    </overlay>

    <overlay
      key="marketSwitcherModal"
      block-class="market-switcher-modal-overlay"
      direction="up"
      namespace="marketSwitcherModal"
      :show-close="false"
      type="modal"
    >
      <template #body="{ isActive }">
        <market-switcher-modal
          v-if="getComponentVisible('market-switcher-modal')"
          v-cloak
          :countries="countries"
          :form="form"
          :is-active="isActive"
          :languages="languages"
        />

        <!-- Market switcher modal loading placeholder -->
        <div
          v-if="!getComponentActive('market-switcher-modal')"
          class="market-switcher__modal critical-component-hide"
        >
          <div class="market-switcher__modal-header">
            <p
              class="text-heading-l"
              v-text="$string('market_switcher.modal.title')"
            />

            <p
              class="text-body-s"
              v-text="$string('market_switcher.modal.text')"
            />
          </div>

          <div class="market-switcher__modal-buttons" />
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Market switcher (market-switcher)
 * -----------------------------------------------------------------------------
 * Global component to manage state and display overlay based on geolocation.
 *
 * @param {Array<Object>} countries - Array of countries from localization.
 * @param {Boolean} enableModal - Enable market switcher modal.
 * @param {Array<Object>} languages - Array of languages from localization.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'

export default {
  name: 'MarketSwitcher',

  components: {
    MarketSwitcherDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/market-switcher-drawer/market-switcher-drawer'
      ),
    }),
    MarketSwitcherModal: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/market-switcher-modal/market-switcher-modal'
      ),
    }),
    Overlay,
  },

  props: {
    countries: {
      type: Array,
      default: () => [],
      required: true,
    },
    enableModal: {
      type: Boolean,
      default: true,
      required: true,
    },
    languages: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  data() {
    return {
      cookiesOpen: true,
      form: false,
      formSelector: '[js-market-switcher="form"]',
      global: true,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      geolocation: (state) => state['market-switcher'].geolocation,
      isMobile: (state) => state.index.screen.mobile,
      preference: (state) => state['market-switcher'].preference,
    }),

    /**
     * Compute current country block based on block's supported codes.
     * @returns {Object}
     */
    currentCountry() {
      return this.countries.find((country) => {
        return country.code === this.$variable('store.country')
      })
    },

    /**
     * Compute current language object based on language code.
     * @returns {Object}
     */
    currentLanguage() {
      return this.languages.find((language) => {
        return language.code === this.$variable('store.language')
      })
    },

    /**
     * Compute default country.
     * @returns {Object}
     */
    defaultCountry() {
      return this.countries.find((country) => {
        return country.default
      })
    },

    /**
     * Compute drawer direction.
     * @returns {String}
     */
    drawerDirection() {
      return this.isMobile ? 'up' : 'left'
    },
  },

  watch: {

    /**
     * Watch cookies open to display modal.
     * - Do not display geolocation modal when cookies are open.
     */
    cookiesOpen: {
      handler() {
        this.handleGeolocationModal()
      },
      immediate: true,
    },

    /**
     * Watch geolocation update to display modal.
     * - Immediate so it can react to persisted state or query.
     */
    geolocation: {
      handler() {
        this.handleGeolocationModal()
      },
      immediate: true,
    },
  },

  mounted() {
    cnvs.EventBus.listen('onetrust:opened', () => {
      this.cookiesOpen = true
    })

    cnvs.EventBus.listen('onetrust:closed', () => {
      this.cookiesOpen = false
    })

    this.form = document.querySelector(this.formSelector)
    this.setCurrent(this.currentCountry)
    this.setCurrent(this.currentLanguage)
    this.queryGeolocation()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setCurrent: 'market-switcher/setCurrent',
      setGeolocation: 'market-switcher/setGeolocation',
      setSuggestedCountry: 'market-switcher/setSuggestedCountry',
    }),

    /**
     * Query geolocation from Shopify geolocation app.
     */
    async queryGeolocation() {
      try {
        if (this.geolocation?.detected_values?.country?.handle) {
          return
        }

        let response = await fetch('/browsing_context_suggestions.json')
        response = await response.json()
        this.setGeolocation(response)

      } catch (error) {
        throw new Error('Failed to fetch geolocation', error)
      }
    },

    /**
     * Handle opening geolocation modal.
     * - Don't open modal if preferences have been set, even if they don't
     *   match the current country.
     * - Don't open if cookies are still open.
     */
    handleGeolocationModal() {
      if (
        !this.enableModal ||
        !this.currentCountry?.code ||
        !this.geolocation?.detected_values ||
        this.preference?.code ||
        this.cookiesOpen
      ) {
        return
      }

      let suggestedCountry = this.countries.find((country) => {
        return country.code === this.geolocation.detected_values?.country?.handle
      })

      /**
       * If no matching country then fallback to default country.
       */
      if (!suggestedCountry) {
        suggestedCountry = this.defaultCountry
      }

      /**
       * Open modal if geolocation doesn't match current country.
       */
      if (
        !suggestedCountry ||
        suggestedCountry?.code === this.currentCountry?.code
      ) {
        return
      }

      this.setSuggestedCountry(suggestedCountry)

      this.openOverlay({
        component: 'market-switcher-modal',
        ignoreDismissed: true,
        namespace: 'marketSwitcherModal',
      })
    },
  },
}
</script>
